import { CommonModule } from '@angular/common'
import {
  Component,
  DestroyRef,
  Signal,
  TemplateRef,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core'
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop'
import { FormControl, ReactiveFormsModule } from '@angular/forms'

import { EMPTY, catchError, debounceTime, filter, map, startWith, tap } from 'rxjs'

import {
  FlxButtonModule,
  FlxFormControlModule,
  FlxModalModule,
  FlxModalService,
  SelectItem,
} from '@flowx/angular-ui-toolkit'
import { defaultJsonEditorOptions } from '@flowx/designer/modules/monaco-code-editor/constants'
import { MonacoEditorModule } from '@flowx/designer/modules/monaco-code-editor/editor.module'
import {
  CodeEditorFlxLanguage,
  ModelMarkerWithId,
} from '@flowx/designer/modules/monaco-code-editor/types'
import { CodeEditorComponent } from '@flowx/designer/modules/script-builder/components/code-editor/code-editor.component'
import { ConfirmationModalComponent } from '@flowx/designer/shared/components/confirmation-modal/confirmation-modal.component'
import { FlxSpinnerModule } from '@flowx/designer/shared/components/spinner'
import { ProcessInstanceService } from '@flowx/designer/shared/services/process-instance.service'
import { ToastMessageService } from '@flowx/designer/shared/services/toast-message.service'

export const editorLanguageSelectOptions: SelectItem<CodeEditorFlxLanguage>[] = [
  { label: 'JSON', value: CodeEditorFlxLanguage.Json },
]

enum VariablesEditorViewMode {
  Edit = 'edit',
  Diff = 'diff',
}

@Component({
  selector: 'app-process-instance-variables-editor-modal',
  imports: [
    FlxModalModule,
    CodeEditorComponent,
    ReactiveFormsModule,
    FlxButtonModule,
    FlxFormControlModule,
    FlxSpinnerModule,
    MonacoEditorModule,
    CommonModule,
  ],
  templateUrl: './process-instance-variables-editor-modal.component.html',
  styleUrls: ['./process-instance-variables-editor-modal.component.scss'],
  host: { class: 'h-full' },
})
export class ProcessInstanceVariablesEditorModalComponent {
  #processInstanceService = inject(ProcessInstanceService)
  #toastMessageService = inject(ToastMessageService)
  #flxModalService = inject(FlxModalService)
  #destroyRef = inject(DestroyRef)

  variables: Signal<Record<string, any>> = input.required()
  readOnly: Signal<boolean> = input(false)
  confirmationModalTemplate: Signal<TemplateRef<any>> = viewChild('confirmationModalTemplate')
  hide = output<void>()

  variablesFC = new FormControl('')
  languageFC = new FormControl<SelectItem<CodeEditorFlxLanguage>>({
    value: editorLanguageSelectOptions[0],
    disabled: true,
  })

  languageSelect = toSignal(this.languageFC.valueChanges.pipe(startWith(this.languageFC.value)))
  editorLanguageSelectOptions = editorLanguageSelectOptions

  codeEditorOptions = computed(() => {
    const language = this.languageSelect()
    return {
      ...defaultJsonEditorOptions,
      language: language.value,
    }
  })

  codeCompareEditorOptions = computed(() => {
    const codeEditorOptions = this.codeEditorOptions()
    return {
      ...codeEditorOptions,
      readOnly: true,
    }
  })

  loader = signal(false)

  canExecuteChanges = toSignal(
    this.variablesFC.statusChanges.pipe(
      startWith(this.variablesFC.status),
      debounceTime(100),
      map((status) => status === 'VALID' && this.variablesFC.dirty)
    )
  )

  viewMode = signal(VariablesEditorViewMode.Edit)
  isEditMode = computed(() => this.viewMode() === VariablesEditorViewMode.Edit)
  isDiffMode = computed(() => this.viewMode() === VariablesEditorViewMode.Diff)

  modifiedVariablesFC = toSignal(this.variablesFC.valueChanges)
  diffView = computed(() => {
    const isEditMode = this.isEditMode()
    const modified = this.modifiedVariablesFC()
    const original = untracked(() => this.variables())

    if (isEditMode) {
      return {
        originalModel: { language: CodeEditorFlxLanguage.Json, code: '' },
        modifiedModel: { language: CodeEditorFlxLanguage.Json, code: '' },
      }
    }
    return {
      originalModel: {
        code: JSON.stringify(original, null, 2),
        language: CodeEditorFlxLanguage.Json,
      },
      modifiedModel: {
        code: modified,
        language: CodeEditorFlxLanguage.Json,
      },
    }
  })

  constructor() {
    effect(() => {
      const variables = this.variables()
      this.variablesFC.setValue(JSON.stringify(variables, null, 2))
    })
  }

  saveChanges(): void {
    this.loader.set(true)
    this.#processInstanceService
      .updateProcessInstanceVariables(JSON.parse(this.variablesFC.value))
      .pipe(
        tap(() => {
          this.hide.emit()
          this.#toastMessageService.showSuccessMessage(
            'Modify Variables operation executed successfully. '
          )
        }),
        takeUntilDestroyed(this.#destroyRef),
        catchError(({ error }) => {
          this.loader.set(false)
          this.#toastMessageService.showErrorMessage(error?.title)
          return EMPTY
        })
      )
      .subscribe()
  }

  onDismiss(): void {
    if (!this.variablesFC.dirty) {
      this.hide.emit()
      return
    }

    this.#flxModalService
      .show(
        ConfirmationModalComponent,
        {
          closeOnBackdropClick: false,
          backdropClass: 'overlay-backdrop',
        },
        {
          title: `Discard <span class="font-bold">Modify Variables</span>`,
          subtitle: `All the configurations will be lost and you will need to start over another Operation.`,
          btnOkText: `Discard`,
          btnOkType: 'danger',
        }
      )
      .data$.pipe(
        filter((data) => data.confirm),
        tap(() => this.hide.emit()),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe()
  }

  setDataValidity(errors: ModelMarkerWithId[]): void {
    if (errors.length > 0) {
      this.variablesFC.setErrors({ jsonInvalid: true })
    } else {
      // Make sure we validate the JSON content as well, not just syntax
      try {
        // Parse JSON to check if it's valid
        JSON.parse(this.variablesFC.value)
        // If no error was thrown, JSON is valid, clear errors
        this.variablesFC.setErrors(null)
      } catch (e) {
        // Invalid JSON
        this.variablesFC.setErrors({ jsonInvalid: true })
      }
    }
  }

  toggleViewMode(): void {
    this.viewMode.update((viewMode) =>
      viewMode === VariablesEditorViewMode.Edit
        ? VariablesEditorViewMode.Diff
        : VariablesEditorViewMode.Edit
    )
  }
}
